import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerLinkSx, partnerOutlineBtnSx, partnerOxbloodBtnSx } from './PartnerListChrome';
import PartnerLogoMark from './PartnerLogoMark';

/**
 * Traveler picks a directory-listed advisor to manage (or take over) their trip.
 */
export default function HandOverTripToAgentDialog({
  open,
  onClose,
  tripId,
  onSuccess,
  mode = 'assign',
  excludePartnerId = null,
}) {
  const { t } = useTranslation();
  const isSwitch = mode === 'switch';
  const [knownAdvisors, setKnownAdvisors] = useState([]);
  const [directoryAdvisors, setDirectoryAdvisors] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadAdvisors = useCallback(async (q = '') => {
    setLoadingList(true);
    setError(null);
    try {
      const [knownRes, dirRes] = await Promise.all([
        api.get('/package-booking/my-advisors').catch(() => ({ data: { advisors: [] } })),
        api.get('/advisors', { params: { q: q || undefined, limit: 40 } }),
      ]);
      setKnownAdvisors(knownRes.data?.advisors || []);
      setDirectoryAdvisors(dirRes.data?.advisors || []);
    } catch (err) {
      console.error('Failed to load advisors for handover:', err);
      setError(t('trip.handoverLoadFailed'));
    } finally {
      setLoadingList(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setMessage('');
    setError(null);
    setSearch('');
    loadAdvisors('');
  }, [open, loadAdvisors]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = setTimeout(() => {
      loadAdvisors(search.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [search, open, loadAdvisors]);

  const options = useMemo(() => {
    const excluded = excludePartnerId != null ? Number(excludePartnerId) : null;
    const knownIds = new Set(
      knownAdvisors.map((a) => Number(a.partnerId)).filter(Boolean)
    );
    const knownById = new Map(
      knownAdvisors.map((a) => [Number(a.partnerId), a])
    );
    return directoryAdvisors
      .map((a) => {
        const id = Number(a.id);
        const known = knownById.get(id);
        return {
          id,
          name: a.companyName || a.contactName || known?.partnerName,
          logo: a.logo || known?.logo || null,
          locationLabel: a.locationLabel || null,
          known: knownIds.has(id),
        };
      })
      .filter((a) => !excluded || a.id !== excluded)
      .sort((a, b) => {
        if (a.known !== b.known) return a.known ? -1 : 1;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
  }, [knownAdvisors, directoryAdvisors, excludePartnerId]);

  const confirmHandover = async () => {
    if (!selected?.id || !tripId) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post(`/trips/${tripId}/assign-partner`, {
        partnerId: selected.id,
        message: message.trim() || undefined,
      });
      if (response?.data) {
        onSuccess?.(response.data);
      }
      onClose?.();
    } catch (err) {
      console.error('Trip handover failed:', err);
      setError(
        err.response?.data?.error
        || t(isSwitch ? 'trip.switchAdvisorFailed' : 'trip.handoverFailed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: DOCUMENT.surface, borderRadius: 0 } }}
      data-testid="trip-handover-dialog"
    >
      <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 24 }}>
        {selected
          ? t(isSwitch ? 'trip.switchAdvisorConfirmTitle' : 'trip.handoverConfirmTitle')
          : t(isSwitch ? 'trip.switchAdvisorTitle' : 'trip.handoverTitle')}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {!selected ? (
          <>
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 2 }}>
              {t(isSwitch ? 'trip.switchAdvisorBody' : 'trip.handoverBody')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('trip.handoverSearch')}
              sx={{ mb: 2 }}
              inputProps={{ 'data-testid': 'trip-handover-search' }}
            />
            {loadingList ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : options.length === 0 ? (
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, py: 2 }}>
                {t('trip.handoverEmpty')}
              </Typography>
            ) : (
              options.map((advisor) => (
                <Button
                  key={advisor.id}
                  fullWidth
                  onClick={() => setSelected(advisor)}
                  sx={{
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    py: 1.25,
                    mb: 1,
                    border: `1px solid ${DOCUMENT.rule}`,
                    color: DOCUMENT.ink,
                    textTransform: 'none',
                    '&:hover': { borderColor: DOCUMENT.accent, bgcolor: DOCUMENT.canvas },
                  }}
                  data-testid={`trip-handover-advisor-${advisor.id}`}
                >
                  <PartnerLogoMark
                    src={advisor.logo}
                    partner={{ company_name: advisor.name }}
                    alt=""
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      bgcolor: DOCUMENT.canvas,
                      border: `1px solid ${DOCUMENT.rule}`,
                      color: DOCUMENT.oxblood,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  />
                  <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 500 }}>{advisor.name}</Typography>
                    {advisor.locationLabel && (
                      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 12 }}>
                        {advisor.locationLabel}
                      </Typography>
                    )}
                    {advisor.known && (
                      <Typography sx={{ color: DOCUMENT.accent, fontSize: 11 }}>
                        {t('trip.handoverKnown')}
                      </Typography>
                    )}
                  </Box>
                </Button>
              ))
            )}
          </>
        ) : (
          <>
            <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 2 }}>
              {t(
                isSwitch ? 'trip.switchAdvisorConfirmBody' : 'trip.handoverConfirmBody',
                { name: selected.name }
              )}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 2,
                p: 1.5,
                border: `1px solid ${DOCUMENT.rule}`,
              }}
            >
              <PartnerLogoMark
                src={selected.logo}
                partner={{ company_name: selected.name }}
                alt=""
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  bgcolor: DOCUMENT.canvas,
                  border: `1px solid ${DOCUMENT.rule}`,
                  color: DOCUMENT.oxblood,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              />
              <Typography sx={{ fontWeight: 500 }}>{selected.name}</Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              label={t('trip.handoverMessageLabel')}
              placeholder={t('trip.handoverMessagePlaceholder')}
              inputProps={{ 'data-testid': 'trip-handover-message', maxLength: 2000 }}
            />
          </>
        )}
        {error && (
          <Typography sx={{ color: 'error.main', fontSize: 13, mt: 2 }} role="alert">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        {selected ? (
          <>
            <Button
              onClick={() => setSelected(null)}
              disabled={submitting}
              sx={partnerLinkSx}
              data-testid="trip-handover-back"
            >
              {t('trip.handoverBack')}
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={onClose} disabled={submitting} sx={partnerOutlineBtnSx}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={confirmHandover}
                disabled={submitting}
                sx={partnerOxbloodBtnSx}
                data-testid="trip-handover-confirm"
              >
                {submitting
                  ? t(isSwitch ? 'trip.switchAdvisorSubmitting' : 'trip.handoverSubmitting')
                  : t(isSwitch ? 'trip.switchAdvisorConfirm' : 'trip.handoverConfirm')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Button
              onClick={() => window.open('/advisors', '_blank', 'noopener,noreferrer')}
              sx={partnerLinkSx}
            >
              {t('trip.handoverBrowse')}
            </Button>
            <Button onClick={onClose} sx={partnerOutlineBtnSx}>
              {t('common.cancel')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
